import { HttpClient } from '@angular/common/http';
import { Directive, ElementRef, EventEmitter, HostListener, Output, Renderer2, inject } from '@angular/core';
import {
  AbstractControl,
  AsyncValidator,
  ControlValueAccessor,
  NG_ASYNC_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
} from '@angular/forms';
import { Bank } from '@zero/shared/util-models';
import { HttpResponseHandleHelper } from '@zero/shared/util-services';
import { environment } from 'environments/environment';
import { isValidIBAN } from 'ibantools';
import { firstValueFrom } from 'rxjs';

@Directive({
  selector: '[zeroIbanInput]',
  providers: [
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: IbanInputDirective,
      multi: true,
    },
    { provide: NG_VALUE_ACCESSOR, useExisting: IbanInputDirective, multi: true },
  ],
  standalone: true,
})
export class IbanInputDirective implements AsyncValidator, ControlValueAccessor {
  readonly #httpClient = inject(HttpClient);
  readonly #renderer = inject(Renderer2);
  readonly #hostElemRef = inject(ElementRef<HTMLInputElement>);

  @Output() public bankName = new EventEmitter<string>();
  @Output() public ongoingRequest = new EventEmitter<boolean>();
  @Output() public directDebitMandatePossible = new EventEmitter<boolean>();
  @Output() public displayDirectDebitMandateNotPossibleHint = new EventEmitter<boolean>();

  public onChangeVal: (val: unknown) => void;
  public onTouched: () => void;

  public static removeAllSpaces(val: string): string {
    return val ? val.replace(/ /g, '') : '';
  }

  public static convertForDisplay(value: string): string {
    if (!value) {
      return '';
    }

    const splitted: string[] = IbanInputDirective.removeAllSpaces(value).toUpperCase().split('');
    let result = '';
    for (let i = 0; i < splitted.length; i++) {
      result += splitted[i];
      if ((i + 1) % 4 === 0 && i < splitted.length - 1) {
        result += ' ';
      }
    }
    return result;
  }

  public async validate(control: AbstractControl): Promise<ValidationErrors | null> {
    const iban = IbanInputDirective.removeAllSpaces(control.value).trim();
    const patternValid = isValidIBAN(iban);

    if (!patternValid) {
      await Promise.resolve();
      const result: ValidationErrors = {
        wrongFormat: true,
      };
      this.bankName.emit('—');
      this.directDebitMandatePossible.emit(false);
      this.displayDirectDebitMandateNotPossibleHint.emit(false);
      return result;
    }

    this.ongoingRequest.emit(true);
    const response = await firstValueFrom(
      HttpResponseHandleHelper.handleResponse(
        this.#httpClient.get<Bank>(`${environment.apihost}/api/bank/${iban}`, {
          params: HttpResponseHandleHelper.skipGlobalHandler(),
        }),
      ),
    );
    this.ongoingRequest.emit(false);
    if (response.success) {
      this.bankName.emit(response.data.bankName);
      this.directDebitMandatePossible.emit(response.data.sepaDirectDebitSupported);
      this.displayDirectDebitMandateNotPossibleHint.emit(!response.data.sepaDirectDebitSupported);
      this.#setFormattedValue(control.value);
      return null;
    } else {
      this.directDebitMandatePossible.emit(false);
      this.displayDirectDebitMandateNotPossibleHint.emit(false);
      return { BLZOK: response.errorMessage };
    }
  }

  public registerOnChange(fn: (val: unknown) => void): void {
    this.onChangeVal = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public writeValue(obj: string): void {
    if (obj) {
      const displayVal = IbanInputDirective.convertForDisplay(obj);
      this.#renderer.setProperty(this.#hostElemRef.nativeElement, 'value', displayVal);
    }
  }

  @HostListener('blur')
  public onBlur(): void {
    this.onTouched();
  }

  @HostListener('input', ['$event'])
  public onInputChange(ev: { target: { value: unknown } }): void {
    this.onChangeVal(ev.target.value);
  }

  #setFormattedValue(valToFormat: string): void {
    const displayVal = IbanInputDirective.convertForDisplay(valToFormat);
    this.#renderer.setProperty(this.#hostElemRef.nativeElement, 'value', displayVal);
  }
}
